import {
  isBackendKnownUnavailable,
  markBackendAvailable,
  markBackendUnavailable,
  onBackendAvailable,
} from '@affine/core/utils/backend-availability';
import { SubscriptionStatus } from '@affine/graphql';
import {
  createSignalFromObservable,
  type Signal,
} from '@blocksuite/affine/shared/utils';
import { signal } from '@preact/signals-core';
import { LiveData, Service } from '@toeverything/infra';

import {
  defaultWorkspaceApiClient,
  invalidateWorkspaceRead,
  readWorkspaceCached,
  type SubscriptionService,
} from '../../cloud';
import type { GlobalStateService } from '../../storage';

const AI_MODEL_ID_KEY = 'AIModelId';
const AI_MODELS_CACHE_KEY_PREFIX = 'AIModelRegistryCache:';

const STREAMLAIN_MANAGED_CHAT_MODEL_IDS = new Set([
  'streamlain-ai-eco',
  'streamlain-ai-standard',
  'streamlain-ai-pro',
]);

function isManagedChatModelId(modelId: string | undefined): boolean {
  return !!modelId && STREAMLAIN_MANAGED_CHAT_MODEL_IDS.has(modelId);
}

export type ReasoningModeId =
  | 'off'
  | 'auto'
  | 'minimal'
  | 'low'
  | 'medium'
  | 'high'
  | 'xhigh'
  | 'max';

export interface ModelReasoningMode {
  id: ReasoningModeId;
  label: string;
  enabled: boolean;
  effort?: 'minimal' | 'low' | 'medium' | 'high' | 'xhigh' | 'max';
  budgetTokens?: number;
  thinkingLevel?: 'minimal' | 'low' | 'medium' | 'high';
}

export interface ModelReasoningProfile {
  label: 'Reasoning';
  strategy: string;
  defaultMode: ReasoningModeId;
  modes: ModelReasoningMode[];
}

/**
 * sl-nagw: Registry model from REST API — single source of truth.
 */
interface RegistryModel {
  id: string;
  name: string;
  providerId: string;
  providerName: string;
  capabilities: string[];
  tier: string;
  status: string;
  supportsReasoning: boolean;
  supportsAdaptiveThinking: boolean;
  usesThinkingLevel: boolean;
  supportsSamplingControls: boolean;
  reasoningProfile: ModelReasoningProfile | null;
  displayMultiplier: number;
  isDefaultForCapability: string | null;
  enabled: boolean;
  workspaceDisabled?: boolean;
}

export interface AIModel {
  name: string;
  id: string;
  version: string;
  category: string;
  isPro: boolean;
  isDefault: boolean;
  supportsReasoning: boolean;
  supportsAdaptiveThinking: boolean;
  usesThinkingLevel: boolean;
  supportsSamplingControls: boolean;
  reasoningProfile: ModelReasoningProfile | null;
  providerId: string;
  providerName: string;
  tier: string;
  costMultiplier: number;
}

/**
 * Local-first registry snapshot persisted per workspace. Hydrated when the
 * backend registry is unreachable so the model selector keeps working
 * offline (stale-while-revalidate).
 */
interface AIModelRegistrySnapshot {
  models: AIModel[];
  hasWebSearchCapability: boolean;
  routingAvailable: boolean;
  savedAt: number;
}

interface SyncChatModelPreferenceOptions {
  overwriteLocal?: boolean;
}

/** sl-sf9o: Sentinel value for automatic model routing */
export const AUTO_MODEL_ID = 'auto';

export class AIModelService extends Service {
  modelId: Signal<string | undefined>;

  models: Signal<AIModel[]> = signal([]);

  loadError: Signal<string | null> = signal(null);

  /**
   * True when the current model list was hydrated from the local snapshot
   * because the backend registry was unreachable (local-first fallback).
   * Cleared on the next successful registry load.
   */
  stale: Signal<boolean> = signal(false);

  /**
   * True when at least one published, enabled model with 'webSearch' capability
   * is available (i.e. provider has API key configured).
   */
  hasWebSearchCapability: Signal<boolean> = signal(false);

  /** sl-sf9o: True when workspace admin has enabled auto-routing */
  routingAvailable: Signal<boolean> = signal(false);

  private readonly modelId$ = LiveData.from(
    this.globalStateService.globalState.watch<string>(AI_MODEL_ID_KEY),
    undefined
  );

  /** Workspace ID for which models are loaded (null = not loaded yet) */
  private _loadedForWorkspace: string | null = null;
  /** Workspace ID currently being loaded */
  private _loadingWorkspaceId: string | null = null;
  private _loadingPromise: Promise<void> | null = null;

  constructor(
    private readonly globalStateService: GlobalStateService,
    private readonly subscriptionService: SubscriptionService
  ) {
    super();

    const { signal: modelId, cleanup } = createSignalFromObservable<
      string | undefined
    >(this.modelId$, undefined);
    this.modelId = modelId;
    this.disposables.push(cleanup);

    // Try to load models immediately if workspace URL is available
    const wsMatch = location.pathname.match(/\/workspace\/([^/]+)/);
    if (wsMatch) {
      this.loadForWorkspace(wsMatch[1]).catch(console.error);
    }

    const disposeBackendAvailable = onBackendAvailable(() => {
      const wsId = this._loadedForWorkspace ?? this._loadingWorkspaceId;
      if (wsId) {
        this.reloadForWorkspace(wsId).catch(console.error);
      } else {
        const match = location.pathname.match(/\/workspace\/([^/]+)/);
        if (match) {
          this.loadForWorkspace(match[1]).catch(console.error);
        }
      }
    });
    this.disposables.push(disposeBackendAvailable);

    // sl-sf9o: Re-sync routing availability when settings change
    const onModelChanged = () => {
      const wsId = this._loadedForWorkspace;
      if (wsId) {
        this.reloadForWorkspace(wsId).catch(console.error);
      }
    };
    window.addEventListener('sl:ai-model-changed', onModelChanged);
    this.disposables.push(() =>
      window.removeEventListener('sl:ai-model-changed', onModelChanged)
    );

    // Subscribe to plan changes — reset pro model on downgrade
    const sub = this.subscriptionService.subscription.activePlan$.subscribe(
      subscription => {
        const isSubscribed = subscription?.status === SubscriptionStatus.Active;
        // sl-sf9o: 'auto' is not a concrete model — skip downgrade check
        if (this.modelId.value === AUTO_MODEL_ID) return;
        const model = this.models.value.find(
          model => model.id === this.modelId.value
        );
        // Plan-based model lock removed — all provisioned models selectable
        void isSubscribed;
        void model;
      }
    );
    this.disposables.push(() => sub.unsubscribe());
  }

  resetModel = () => {
    this.globalStateService.globalState.set(AI_MODEL_ID_KEY, undefined);
  };

  setModel = (modelId: string) => {
    // sl-sf9o: 'auto' is always allowed (backend handles routing)
    if (modelId === AUTO_MODEL_ID) {
      this.globalStateService.globalState.set(AI_MODEL_ID_KEY, modelId);
      return;
    }
    // Plan-based model lock removed — all provisioned models selectable
    this.globalStateService.globalState.set(AI_MODEL_ID_KEY, modelId);
  };

  syncChatModelPreferenceForWorkspace = async (
    workspaceId: string,
    options: SyncChatModelPreferenceOptions = {}
  ): Promise<string | undefined> => {
    if (!options.overwriteLocal && this.modelId.value) {
      return this.modelId.value;
    }

    const userPreference = await this.fetchUserChatModelPreference();
    if (userPreference) {
      this.setModel(userPreference);
      return userPreference;
    }

    const workspaceDefaults = await this.fetchWorkspaceDefaults(workspaceId);
    const workspaceDefault = workspaceDefaults?.chatModel ?? undefined;
    if (workspaceDefault) {
      this.setModel(workspaceDefault);
      return workspaceDefault;
    }

    return undefined;
  };

  resolveChatModelIdForWorkspace = async (
    workspaceId: string
  ): Promise<string | undefined> => {
    const preferredModel =
      await this.syncChatModelPreferenceForWorkspace(workspaceId);
    await this.loadForWorkspace(workspaceId);

    return (
      preferredModel ??
      this.modelId.value ??
      this.models.value.find(model => model.isDefault)?.id ??
      this.models.value[0]?.id
    );
  };

  /**
   * Load models for a workspace. Idempotent — skips if already loaded
   * for the same workspace. Deduplicates concurrent calls.
   */
  loadForWorkspace = (workspaceId: string): Promise<void> => {
    // Already loaded for this workspace with a usable model selection.
    // A prior load can legitimately finish with an empty model list (for
    // example during backend warm-up); do not cache that as usable for later
    // in-doc AI requests.
    if (this.hasUsableModelSelection(workspaceId)) {
      return Promise.resolve();
    }
    if (this._loadedForWorkspace === workspaceId) {
      this._loadedForWorkspace = null;
    }
    // Already loading for this workspace — return existing promise
    if (this._loadingPromise && this._loadingWorkspaceId === workspaceId) {
      return this._loadingPromise;
    }

    this._loadingWorkspaceId = workspaceId;
    this._loadingPromise = this._doLoad(workspaceId);
    return this._loadingPromise;
  };

  private readonly hasUsableModelSelection = (workspaceId: string): boolean => {
    if (this._loadedForWorkspace !== workspaceId) return false;
    // Snapshot-hydrated model lists must keep revalidating against the backend
    if (this.stale.value) return false;
    // The registry response is the authoritative proof that this workspace was
    // initialized. GlobalState propagation of the selected default can lag a
    // render behind it; do not discard the fresh registry and refetch both
    // bootstrap endpoints during that gap.
    if (this.models.value.length > 0) return true;
    if (this.modelId.value === AUTO_MODEL_ID) return true;
    if (isManagedChatModelId(this.modelId.value)) return true;
    return (
      !!this.modelId.value &&
      this.models.value.some(model => model.id === this.modelId.value)
    );
  };

  private readonly reloadForWorkspace = (
    workspaceId: string
  ): Promise<void> => {
    invalidateWorkspaceRead(workspaceId, 'ai-registry-models');
    invalidateWorkspaceRead(workspaceId, 'ai-defaults');
    this._loadedForWorkspace = null;
    return this.loadForWorkspace(workspaceId);
  };

  private readonly _doLoad = async (workspaceId: string): Promise<void> => {
    try {
      this.loadError.value = null;
      if (isBackendKnownUnavailable()) {
        this.applyOfflineFallback(workspaceId);
        return;
      }
      const fresh = await this.initModels(workspaceId);
      this._loadedForWorkspace = workspaceId;
      if (fresh) {
        this.stale.value = false;
        this.persistRegistrySnapshot(workspaceId);
      }

      // Auto-select default model if no model was previously chosen
      if (!this.modelId.value && this.models.value.length > 0) {
        const defaultModel = this.models.value.find(m => m.isDefault);
        if (defaultModel) {
          this.globalStateService.globalState.set(
            AI_MODEL_ID_KEY,
            defaultModel.id
          );
        }
      }
      if (
        fresh &&
        this.modelId.value &&
        this.modelId.value !== AUTO_MODEL_ID &&
        !isManagedChatModelId(this.modelId.value) &&
        !this.models.value.some(model => model.id === this.modelId.value)
      ) {
        const defaultModel = this.models.value.find(m => m.isDefault);
        this.globalStateService.globalState.set(
          AI_MODEL_ID_KEY,
          defaultModel?.id
        );
      }
    } catch (err) {
      markBackendUnavailable(err);
      if (!this.applyOfflineFallback(workspaceId)) {
        this.models.value = [];
        this.loadError.value =
          err instanceof Error ? err.message : 'Failed to load AI models';
      }
    } finally {
      this._loadingPromise = null;
      this._loadingWorkspaceId = null;
    }
  };

  /**
   * Local-first fallback when the registry is unreachable: keep the model
   * list already in memory, otherwise hydrate the persisted snapshot for
   * this workspace. Returns true when a usable (stale) list is available.
   */
  private readonly applyOfflineFallback = (workspaceId: string): boolean => {
    if (this.models.value.length > 0 || this.hydrateFromSnapshot(workspaceId)) {
      this.stale.value = true;
      this.loadError.value = null;
      return true;
    }
    return false;
  };

  private readonly snapshotKeyFor = (workspaceId: string): string =>
    `${AI_MODELS_CACHE_KEY_PREFIX}${workspaceId}`;

  private readonly persistRegistrySnapshot = (workspaceId: string): void => {
    const snapshot: AIModelRegistrySnapshot = {
      models: this.models.value,
      hasWebSearchCapability: this.hasWebSearchCapability.value,
      routingAvailable: this.routingAvailable.value,
      savedAt: Date.now(),
    };
    this.globalStateService.globalState.set(
      this.snapshotKeyFor(workspaceId),
      snapshot
    );
  };

  private readonly hydrateFromSnapshot = (workspaceId: string): boolean => {
    const snapshot = this.globalStateService.globalState.get<
      AIModelRegistrySnapshot | undefined
    >(this.snapshotKeyFor(workspaceId));
    if (!snapshot || !Array.isArray(snapshot.models)) return false;
    if (snapshot.models.length === 0) return false;
    this.models.value = snapshot.models;
    this.hasWebSearchCapability.value = snapshot.hasWebSearchCapability;
    this.routingAvailable.value = snapshot.routingAvailable;
    return true;
  };

  /**
   * sl-nagw: Single source of truth — REST Registry API.
   * Filters to chat-capable, published, enabled models only.
   * Also detects webSearch capability for toggle visibility.
   *
   * sl-zbyq: Workspace defaults override platform defaults.
   * Priority: Workspace override > Platform default > first model.
   */
  private readonly initModels = async (
    workspaceId: string
  ): Promise<boolean> => {
    const [registryModels, wsDefaults] = await Promise.all([
      this.fetchRegistryModels(workspaceId),
      this.fetchWorkspaceDefaults(workspaceId),
    ]);
    if (registryModels.length === 0) return false;

    // Detect webSearch capability: at least one published, enabled webSearch model
    this.hasWebSearchCapability.value = registryModels.some(
      m =>
        m.capabilities.includes('webSearch') &&
        m.status !== 'deprecated' &&
        m.enabled &&
        !m.workspaceDisabled
    );

    // Filter: only chat-capable, non-deprecated, enabled models
    const chatModels = registryModels.filter(
      m =>
        m.capabilities.includes('chat') &&
        m.status !== 'deprecated' &&
        m.enabled &&
        !m.workspaceDisabled
    );

    // Resolve effective default: workspace override > platform default > first model
    const platformDefaultId =
      chatModels.find(m => m.isDefaultForCapability === 'chat')?.id ?? null;
    const workspaceDefaultId =
      chatModels.find(m => m.id === wsDefaults?.chatModel)?.id ?? null;
    const defaultId =
      workspaceDefaultId ?? platformDefaultId ?? chatModels[0]?.id;

    this.models.value = chatModels.map(model => {
      const [category] = model.name.split(' ');
      const version = model.name.slice(category.length + 1);
      return {
        name: model.name,
        id: model.id,
        version,
        category,
        isPro: model.tier === 'professional',
        isDefault: model.id === defaultId,
        supportsReasoning: model.supportsReasoning,
        supportsAdaptiveThinking: model.supportsAdaptiveThinking,
        usesThinkingLevel: model.usesThinkingLevel,
        supportsSamplingControls: model.supportsSamplingControls,
        reasoningProfile: model.reasoningProfile,
        providerId: model.providerId,
        providerName: model.providerName.replace(/\s*\(.*?\)\s*$/, ''),
        tier: model.tier,
        costMultiplier: model.displayMultiplier,
      };
    });
    return true;
  };

  private readonly fetchRegistryModels = async (
    workspaceId: string
  ): Promise<RegistryModel[]> => {
    if (isBackendKnownUnavailable()) {
      return [];
    }

    try {
      const data = await readWorkspaceCached({
        workspaceId,
        key: 'ai-registry-models',
        load: () =>
          defaultWorkspaceApiClient.requestJson<{
            models?: RegistryModel[];
          }>({
            workspaceId,
            path: `/api/workspace/${encodeURIComponent(workspaceId)}/ai/registry/models`,
            category: 'ai-bootstrap',
            key: 'ai-registry-models',
            mode: 'background',
          }),
      });
      markBackendAvailable();
      return data.models ?? [];
    } catch (err) {
      markBackendUnavailable(err);
      if (err instanceof Error) {
        throw err;
      }
      throw new Error('Failed to load AI models');
    }
  };

  /**
   * sl-zbyq: Fetch workspace-level AI defaults (overrides platform defaults).
   */
  private readonly fetchWorkspaceDefaults = async (
    workspaceId: string
  ): Promise<{ chatModel: string | null; routingMode?: string } | null> => {
    if (isBackendKnownUnavailable()) {
      return null;
    }

    try {
      const data = await readWorkspaceCached({
        workspaceId,
        key: 'ai-defaults',
        load: () =>
          defaultWorkspaceApiClient.requestJson<{
            defaults?: {
              chatModel: string | null;
              routingMode?: string;
            } | null;
          }>({
            workspaceId,
            path: `/api/workspace/${encodeURIComponent(workspaceId)}/ai-defaults`,
            category: 'ai-bootstrap',
            key: 'ai-defaults',
            mode: 'background',
          }),
      });
      markBackendAvailable();
      const defaults = data.defaults ?? null;
      // sl-sf9o: Update routing availability from workspace config
      this.routingAvailable.value = defaults?.routingMode === 'auto';
      // Reset 'auto' selection if routing is not available (admin disabled, page load, etc.)
      if (
        !this.routingAvailable.value &&
        this.modelId.value === AUTO_MODEL_ID
      ) {
        this.resetModel();
      }
      return defaults;
    } catch (error) {
      markBackendUnavailable(error);
      return null;
    }
  };

  private readonly fetchUserChatModelPreference = async (): Promise<
    string | null
  > => {
    if (isBackendKnownUnavailable()) {
      return null;
    }

    try {
      const data = await defaultWorkspaceApiClient.requestJson<{
        preferences?: { chat?: string };
      }>({
        workspaceId: 'user',
        path: '/api/copilot/user-keys/model-preferences',
        category: 'settings',
        key: 'user-model-preferences',
        mode: 'background',
      });
      markBackendAvailable();
      return typeof data.preferences?.chat === 'string'
        ? data.preferences.chat
        : null;
    } catch (error) {
      markBackendUnavailable(error);
      return null;
    }
  };
}
